/**
 * Turning an EPIC map plan into a PNG the drafter can save.
 *
 * This is the only part of EPIC output that is not pure: every decision about what to draw
 * and how heavily lives in `@plan-forge/conversion-core`'s `planEpicMap`, so it is unit
 * tested and a server-side renderer could reuse it untouched. What is here is the canvas
 * call and the save.
 *
 * **Rendering in the browser is deliberate, and it is what the desktop application did.**
 * EPIC output is an image rendered from geometry the browser is already holding - it has the
 * whole `RecognitionResult` in memory to draw the overlay in step 7 - so a server route
 * would upload the geometry back to something that already has it, rasterise it in a Lambda
 * that would need a raster stack in its bundle, and hand back a presigned URL. It buys an
 * addressable S3 object and a `ReviewedBy` record; nothing asks for either for an image, and
 * the X1 package remains the artifact that carries the approval.
 */

import { planEpicMap, POINTS_TO_PIXELS, type EpicGeometry, type EpicMapPlan } from "@plan-forge/conversion-core";
import type { Point, Size } from "@plan-forge/conversion-core";

/**
 * Browsers cap canvas area and **fail by producing a blank or black bitmap rather than
 * throwing**, which would hand somebody an empty EPIC map that looks like a recognition that
 * found nothing. Chrome and Firefox allow well over this on a desktop; the conservative
 * number is mobile Safari's, and being told the sheet is too large beats being given a white
 * rectangle. A full 6048x4320 sheet is 26 Mpx and passes.
 */
const MAX_CANVAS_PIXELS = 64_000_000;

export class EpicRenderError extends Error {}

export interface RenderedEpicMap {
  readonly blob: Blob;
  readonly size: Size;
  /** How many walls were drawn. Shown to the operator, because zero is worth knowing. */
  readonly wallCount: number;
}

/**
 * Renders the plan to a PNG.
 *
 * Draw order is the desktop exporter's: paint the ground in device space, then translate into
 * preview space so the polygon and the geometry can be used as the engine reported them, and
 * clip inside that transform. Getting the clip on the wrong side of the translate offsets the
 * mask from the linework, which reads as a plan cropped to the wrong place.
 */
export async function renderEpicMap(plan: EpicMapPlan): Promise<RenderedEpicMap> {
  const { width, height } = plan.size;
  if (width * height > MAX_CANVAS_PIXELS) {
    throw new EpicRenderError(
      `The conversion area is ${width}x${height} pixels, which is larger than this browser will render in one image. ` +
        "Draw a smaller conversion polygon and run recognition again.",
    );
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  if (ctx === null) throw new EpicRenderError("This browser did not provide a 2D canvas context.");

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);

  ctx.save();
  ctx.translate(-plan.crop.x, -plan.crop.y);

  if (plan.clipPolygon !== null) {
    tracePolygon(ctx, plan.clipPolygon);
    ctx.clip();
  }

  // Butt caps, which is WPF's Pen default and therefore what the desktop output had. Canvas
  // agrees by default; stated because a round cap would fatten every wall end by half its
  // width and quietly change every drawing.
  ctx.lineCap = "butt";
  ctx.strokeStyle = "#000000";

  for (const segment of plan.segments) {
    ctx.lineWidth = segment.widthPoints * POINTS_TO_PIXELS;
    ctx.beginPath();
    ctx.moveTo(segment.start.x, segment.start.y);
    ctx.lineTo(segment.end.x, segment.end.y);
    ctx.stroke();
  }

  ctx.restore();

  return { blob: await toPng(canvas), size: plan.size, wallCount: plan.segments.length };
}

function tracePolygon(ctx: CanvasRenderingContext2D, polygon: readonly Point[]): void {
  ctx.beginPath();
  ctx.moveTo(polygon[0]!.x, polygon[0]!.y);
  for (let i = 1; i < polygon.length; i += 1) {
    ctx.lineTo(polygon[i]!.x, polygon[i]!.y);
  }
  ctx.closePath();
}

function toPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      // Null means the encode failed, which is the other way an over-large canvas shows up.
      if (blob === null) reject(new EpicRenderError("The browser could not encode the EPIC map as a PNG."));
      else resolve(blob);
    }, "image/png");
  });
}

/**
 * Plans and renders in one call, from the shape the app already holds.
 */
export async function buildEpicMap(
  geometry: EpicGeometry,
  polygon: readonly Point[],
  preview: Size,
): Promise<RenderedEpicMap> {
  return renderEpicMap(planEpicMap(geometry, polygon, preview));
}
